// Define the global prefix for all custom CSS variables within the application.
// This helps prevent conflicts with standard CSS properties or other libraries.
export const prefix = "oo";

// --- CORE COLOR PALETTES ---

// Standard base colors defined by Ant Design's color system (excluding gray/neutral).
// These names correspond to the primary colors used for generating 10 shades (50 to 950).
export const colors = [
	"blue",
	"purple",
	"cyan",
	"green",
	"magenta",
	"pink",
	"red",
	"orange",
	"yellow",
	"volcano",
	"geekblue",
	"gold",
	"lime",
];

// --- PRODUCT LEVEL COLOR SYSTEM (Semantic Colors) ---

// Brand/Primary colors derived from the main color palette. These are used for
// the main interactive elements (buttons, links, focus states).
export const brandColors = [
	"colorPrimary", // Primary brand color
	"colorPrimaryBg", // Background color for primary elements (lightest shade)
	"colorPrimaryBgHover", // Background color on hover
	"colorPrimaryBorder", // Border color
	"colorPrimaryBorderHover", // Border color on hover
	"colorPrimaryHover", // Primary color on hover
	"colorPrimaryActive", // Primary color on active state
	"colorPrimaryTextHover", // Text color on hover
	"colorPrimaryText", // Standard text color using the primary palette
	"colorPrimaryTextActive", // Text color on active state
];

// Success colors for positive feedback and confirmation messages.
export const successColors = [
	"colorSuccess",
	"colorSuccessBg",
	"colorSuccessBgHover",
	"colorSuccessBorder",
	"colorSuccessBorderHover",
	"colorSuccessHover",
	"colorSuccessActive",
	"colorSuccessTextHover",
	"colorSuccessText",
	"colorSuccessTextActive",
];

// Warning colors for caution and alerts that do not halt operation.
export const warningColors = [
	"colorWarning",
	"colorWarningBg",
	"colorWarningBgHover",
	"colorWarningBorder",
	"colorWarningBorderHover",
	"colorWarningHover",
	"colorWarningActive",
	"colorWarningTextHover",
	"colorWarningText",
	"colorWarningTextActive",
];

// Error colors for critical issues, validation failures, or destructive actions.
export const errorColors = [
	"colorError",
	"colorErrorBg",
	"colorErrorBgHover",
	"colorErrorBorder",
	"colorErrorBorderHover",
	"colorErrorHover",
	"colorErrorActive",
	"colorErrorTextHover",
	"colorErrorText",
	"colorErrorTextActive",
];

// Info colors, typically used for informational messages or non-critical state changes.
export const infoColors = [
	"colorInfo",
	"colorInfoBg",
	"colorInfoBgHover",
	"colorInfoBorder",
	"colorInfoBorderHover",
	"colorInfoHover",
	"colorInfoActive",
	"colorInfoTextHover",
	"colorInfoText",
	"colorInfoTextActive",
];

// Aggregated list of all functional/semantic colors.
export const functionalColors = [
	...successColors,
	...warningColors,
	...errorColors,
	...infoColors,
];

// --- NEUTRAL/GRAYSCALE COLORS ---

// Neutral colors covering text, background, border, and fill states.
// These are essential for defining the overall look and readability of the UI.
export const neutralColors = [
	"colorTextSolid",
	"colorText", // Default text color
	"colorTextSecondary", // Secondary/subtle text color
	"colorTextTertiary", // Tertiary text color (e.g., placeholder text)
	"colorTextQuaternary", // Least prominent text color

	// Component container background colors
	"colorBgContainer", // Standard background for components (e.g., cards, modals)
	"colorBgElevated", // Background for elevated elements (e.g., dropdowns, popovers)

	// Layout background color (main application background)
	"colorBgLayout",

	"colorBgSpotlight", // Background for spotlight/tooltip effects
	"colorBgMask", // Background for modal masks

	// Border colors
	"colorBorder", // Primary border color
	"colorBorderSecondary", // Secondary/subtle border color

	// Fill colors (e.g., for backgrounds of input fields or disabled elements)
	"colorFill", // Primary fill color
	"colorFillSecondary", // Secondary fill color
	"colorFillTertiary", // Tertiary fill color
	"colorFillQuaternary", // Quaternary fill color
];

// --- AGGREGATE COLOR LISTS ---

// Combines brand and functional colors into a single list representing all product-level semantic colors.
export const productLevelColorSystem = [
	...brandColors,
	...functionalColors,
];

// --- PALETTE SHADE DEFINITIONS ---

// The standard numbers used for defining color shades/steps (Ant Design uses 1-10 for semantic colors, 50-950 for utility colors).
export const colorPaletteNumbers = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

// The number of color variants (shades) generated for each base color.
export const colorVariantsCount = 10;

// Generates an array of all possible base color names, e.g., ['blue', 'blue-1', 'blue-2', ..., 'blue-10', 'purple',...]
// This is likely used by the CSS generation logic (e.g., in `css-variables.ts`) to loop through all shades.
export const baseColorPalettes = colors.flatMap(color =>
	[color, ...Array.from({ length: colorVariantsCount }, (_, i) => `${color}-${i + 1}`)],
);

/**
 * antd's tokens do not provide colors for the gray series (neutral palette)
 *
 * This array provides explicit HEX values for the Neutral Color Palette (Grays),
 * which are necessary because standard Ant Design tokens may not expose them in the required format.
 *
 * Related Issues:
 * @see https://github.com/ant-design/ant-design/pull/31129
 * @see https://github.com/ant-design/ant-design/discussions/45411
 * @see https://github.com/ant-design/ant-design/issues/44247
 *
 * Color Source:
 * @see https://ant.design/docs/spec/colors#neutral-color-palette
 */
export const neutralColorPalettes = ["#fafafa", "#f5f5f5", "#f0f0f0", "#d9d9d9", "#bfbfbf", "#8c8c8c", "#595959", "#434343", "#262626", "#1f1f1f", "#141414"];
